import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Users,
  Trophy,
  Activity,
  ChevronDown,
  Network,
  Target,
  GitCompare,
  Flag,
  Briefcase,
  Radar as RadarIcon,
  LogOut,
  ShieldCheck,
  CircleUser,
  Bookmark,
  Building2,
  Cpu,
  KeyRound,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { WhipLogo } from "./WhipLogo";
import { MobileNav } from "./MobileNav";
import { useAuth } from "@/context/AuthContext";

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isActive = (path) => {
    if (path === "/" && location.pathname === "/") return true;
    if (path !== "/" && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Primary nav per the WHIP brief: Dashboard, Legislation, Profiles, Rankings, Signals.
  // Everything else lives under the "More" dropdown so the primary row stays calm.
  const links = [
    { to: "/", testId: "nav-dashboard", label: "Dashboard", icon: LayoutDashboard, match: () => isActive("/") },
    { to: "/legislation", testId: "nav-bills", label: "Legislation", icon: FileText, match: () => isActive("/legislation") || location.pathname.startsWith("/bill") },
    { to: "/legislators", testId: "nav-legislators", label: "Profiles", icon: Users, match: () => isActive("/legislators") || isActive("/legislator") },
    { to: "/rankings", testId: "nav-rankings", label: "Rankings", icon: Trophy, match: () => isActive("/rankings") },
    { to: "/signals", testId: "nav-signals", label: "Signals", icon: Activity, match: () => isActive("/signals") },
    { to: "/radar", testId: "nav-radar", label: "Radar", icon: RadarIcon, match: () => isActive("/radar") },
  ];

  const moreLinks = [
    { to: "/factions", testId: "nav-more-factions", label: "Backroom", icon: Network, blurb: "Legislator network intelligence" },
    { to: "/compare", testId: "nav-more-compare", label: "Compare", icon: GitCompare, blurb: "Side-by-side legislators" },
    { to: "/watchlist", testId: "nav-more-watchlist", label: "Watchlist", icon: Bookmark, blurb: "Saved bills & legislators" },
    { to: "/parties", testId: "nav-more-parties", label: "Parties", icon: Flag, blurb: "Party-level breakdowns" },
    { to: "/committees", testId: "nav-more-committees", label: "Committees", icon: Briefcase, blurb: "Chamber committees" },
    { to: "/accuracy", testId: "nav-more-accuracy", label: "Model Accuracy", icon: Target, blurb: "Forecasts vs recorded floor votes" },
  ];
  const moreActive = moreLinks.some((m) => isActive(m.to));

  return (
    <nav
      className="sticky top-0 z-50 border-b backdrop-blur-md"
      style={{
        background: "rgba(11,11,15,0.85)",
        borderColor: "var(--whip-border)",
      }}
      data-testid="whip-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <WhipLogo size={26} />
          </Link>

          {/* Mobile: hamburger drawer (desktop row would overflow the viewport) */}
          <MobileNav links={links} moreLinks={moreLinks} isActive={isActive} user={user}
            onLogout={async () => { await logout(); navigate("/about"); }} />

          <div className="hidden lg:flex items-center gap-1">
            {links.map(({ to, testId, label, icon: Icon, match }) => {
              const active = match();
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={testId}
                  className={`relative flex items-center gap-2 px-3.5 py-2 rounded-md text-[13px] font-medium tracking-wide transition-colors ${
                    active
                      ? "text-white"
                      : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                  {active && (
                    <span
                      aria-hidden
                      className="absolute -bottom-[17px] left-2 right-2 h-[2px] rounded-full"
                      style={{
                        background:
                          "linear-gradient(90deg,#FF6A00,#FFA500)",
                        boxShadow: "0 0 12px rgba(255,138,0,0.6)",
                      }}
                    />
                  )}
                </Link>
              );
            })}

            {/* More ▾, secondary routes (Alliance Map / Compare / Parties / Committees) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="nav-more-trigger"
                  className={`relative flex items-center gap-1.5 px-3.5 py-2 rounded-md text-[13px] font-medium tracking-wide transition-colors outline-none ${
                    moreActive
                      ? "text-white"
                      : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  More
                  <ChevronDown className="w-3.5 h-3.5" />
                  {moreActive && (
                    <span
                      aria-hidden
                      className="absolute -bottom-[17px] left-2 right-2 h-[2px] rounded-full"
                      style={{
                        background: "linear-gradient(90deg,#FF6A00,#FFA500)",
                        boxShadow: "0 0 12px rgba(255,138,0,0.6)",
                      }}
                    />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={8}
                className="w-64 p-1 border-white/10"
                style={{
                  background: "rgba(18,18,26,0.98)",
                  backdropFilter: "blur(12px)",
                }}
                data-testid="nav-more-menu"
              >
                {moreLinks.map(({ to, testId, label, icon: Icon, blurb }) => {
                  const active = isActive(to);
                  return (
                    <DropdownMenuItem
                      key={to}
                      asChild
                      className="focus:bg-white/5 cursor-pointer rounded-md px-2.5 py-2"
                    >
                      <Link to={to} data-testid={testId} className="flex items-start gap-3">
                        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${active ? "text-orange-400" : "text-white/70"}`} />
                        <div className="flex-1 min-w-0">
                          <div className={`text-[13px] font-semibold leading-tight ${active ? "text-orange-200" : "text-white"}`}>
                            {label}
                          </div>
                          <div className="text-[11px] text-white/50 leading-snug mt-0.5">
                            {blurb}
                          </div>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User menu */}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="nav-user-trigger"
                    className="ml-2 flex items-center gap-2 px-2.5 py-1.5 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-orange-500/40 transition-colors outline-none"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                    ) : (
                      <CircleUser className="w-5 h-5" />
                    )}
                    <span className="text-[12px] font-medium max-w-[110px] truncate hidden sm:block">
                      {user.name || user.email}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  sideOffset={8}
                  className="w-56 p-1 border-white/10"
                  style={{ background: "rgba(18,18,26,0.98)", backdropFilter: "blur(12px)" }}
                  data-testid="nav-user-menu"
                >
                  <div className="px-2.5 py-2">
                    <p className="text-[13px] font-semibold text-white truncate">{user.name || "n/a"}</p>
                    <p className="text-[11px] text-white/45 font-mono truncate">{user.email}</p>
                    {user.organization_name && (
                      <p className="text-[11px] text-white/55 mt-1 flex items-center gap-1 truncate" data-testid="nav-org-name">
                        <Building2 className="w-3 h-3 shrink-0" /> {user.organization_name}
                      </p>
                    )}
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <span className="inline-flex items-center rounded-full border border-orange-500/40 bg-orange-500/10 px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider text-orange-300" data-testid="nav-tier-badge">
                        WHIP {(user.subscription_tier || "public").toUpperCase()}
                      </span>
                      {user.founding_user && (
                        <span className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider text-amber-300" data-testid="nav-founding-badge">
                          ★ Founding Member
                        </span>
                      )}
                    </div>
                  </div>
                  <DropdownMenuSeparator className="bg-white/10" />
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild className="focus:bg-white/5 cursor-pointer rounded-md px-2.5 py-2">
                      <Link to="/admin/users" data-testid="nav-user-admin" className="flex items-center gap-2.5 text-[13px] text-white">
                        <ShieldCheck className="w-4 h-4 text-orange-400" /> User access
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild className="focus:bg-white/5 cursor-pointer rounded-md px-2.5 py-2">
                      <Link to="/admin/system" data-testid="nav-user-admin-system" className="flex items-center gap-2.5 text-[13px] text-white">
                        <Cpu className="w-4 h-4 text-orange-400" /> System health
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.hasPassword && (
                    <DropdownMenuItem asChild className="focus:bg-white/5 cursor-pointer rounded-md px-2.5 py-2">
                      <Link to="/account/password" data-testid="nav-user-change-password" className="flex items-center gap-2.5 text-[13px] text-white">
                        <KeyRound className="w-4 h-4 text-white/60" /> Change password
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild className="focus:bg-white/5 cursor-pointer rounded-md px-2.5 py-2">
                    <Link to="/privacy" data-testid="nav-user-privacy" className="flex items-center gap-2.5 text-[13px] text-white">
                      <ShieldCheck className="w-4 h-4 text-white/60" /> Privacy & data
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="focus:bg-white/5 cursor-pointer rounded-md px-2.5 py-2 flex items-center gap-2.5 text-[13px] text-white"
                    data-testid="nav-user-logout"
                    onClick={async () => { await logout(); navigate("/about"); }}
                  >
                    <LogOut className="w-4 h-4 text-white/60" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
